import { debugLog } from "../config";

const safeImageProtocols = new Set(["http:", "https:"]);

/** Encode an untrusted server-returned ticker for safe use in a URL path segment. */
export function safeEventTicker(ticker: string): string {
  return encodeURIComponent(ticker);
}

/**
 * Return a normalized URL string if `raw` parses to an absolute `http:` or
 * `https:` URL with no embedded credentials; otherwise `null`.
 *
 * Relative and protocol-relative URLs are rejected (they fail `new URL`
 * without a base) so the host page cannot influence where the widget loads
 * images from. Credentialed URLs (`https://user:pass@host/...`) are rejected
 * because the backend has no reason to emit them and they would leak through
 * to `<img src>` logs and cache keys.
 *
 * The returned string is `parsed.href` rather than `raw` so consumers get a
 * URL-spec-normalized value (lowercased host, default port stripped, leading
 * whitespace trimmed, percent-encoding canonicalized).
 */
export function safeImageUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    // Do not log `raw` — unparseable input may include credentials, tokens,
    // or other arbitrary publisher-supplied content.
    debugLog("safeImageUrl: rejected unparseable url");
    return null;
  }
  if (!safeImageProtocols.has(parsed.protocol)) {
    debugLog(`safeImageUrl: rejected protocol "${parsed.protocol}"`);
    return null;
  }
  if (parsed.username || parsed.password) {
    debugLog("safeImageUrl: rejected credentialed url");
    return null;
  }
  return parsed.href;
}
